import React, { Fragment, useRef, useCallback } from "react";
import useSound from "use-sound";

import { AssetVehicle, CredentialType, UserType, PaymentTypes, IContext, RequestStatus } from "../logic/api";
import { ISessionState } from "./useSession";
import { IDropDownListItem } from "../components/editable/EditableField/fields/DropDownField";

import goodSfx from '../assets/good.mp3';
import badSfx from '../assets/bad.mp3';
import swapSfx from '../assets/swap.mp3';
import busySfx from '../assets/busy.mp3';
import warningSfx from '../assets/warning.mp3';

const defaultDateFormat = "%d/%m %H:%I";

export enum SoundType {
  Good = 'good',
  Bad = 'bad',
  Swap = 'swap',
  Busy = 'busy',
  Warning = 'warning'
}
type PlaySoundOptions = {
  forceSoundEnabled?: boolean;
};

export function useSoundEffects() {
  const [goodSound] = useSound(goodSfx);
  const [badSound] = useSound(badSfx);
  const [swapSound] = useSound(swapSfx);
  const [busySound] = useSound(busySfx);
  const [warningSound] = useSound(warningSfx);

  const playSound = (type: SoundType, options: PlaySoundOptions = { forceSoundEnabled: true }) => {
    const { forceSoundEnabled } = options;
    switch (type) {
      case SoundType.Good:
        goodSound({ forceSoundEnabled });
        break;
      case SoundType.Bad:
        badSound({ forceSoundEnabled });
        break;
      case SoundType.Swap:
        swapSound({ forceSoundEnabled });
        break;
      case SoundType.Busy:
        busySound({ forceSoundEnabled });
        break;
      case SoundType.Warning:
        warningSound({ forceSoundEnabled });
        break;
      default:
        break;
    }
  };

  return playSound;
}

export const getDisplayTaxValue = (flagIsOn: boolean, prePaid: boolean, value: number, session: ISessionState) => {
  if (flagIsOn) {
    return formatCurrency(value, session);
  }
  return prePaid ? "-" : formatCurrency(value, session);
}

export const getDisplayTaxRate = (flagIsOn: boolean, prePaid: boolean, rate: number | undefined) => {
  if (rate === undefined) {
    return "";
  }
  if (flagIsOn) {
    return formatPercentage(rate);
  }
  return prePaid ? "-" : formatPercentage(rate);
};

export const formatPercentage = (value: number | string | unknown | null) => {
  if (value == null) {
    return "";
  }
  return parseFloat((value as string).toString()).toFixed(2) + "%"
}
export const formatCurrency = (value: number | string | null, session: ISessionState) => {
  if (value == null) {
    return "";
  }
  if (session.current.site == null) {
    return "";
  }
  const float = parseFloat(value.toString());
  if (isNaN(float)) {
    return 0;
  }
  const val = float.toFixed(2);
  /*    if(val[val.length-1]!="0"){
         //Always round up to the nearest penny.
         val = (parseFloat(value.toString())+0.01).toFixed(2);
     }else{
         val = parseFloat(value.toString()).toFixed(2);
     } */
  //val = parseFloat(value.toString()).toFixed(2);
  return `${session.current.site.preferences.currency}${val}`;
}

export const formatDateTime = (d: Date | string | null, session: ISessionState) => {
  const date = formatDate(d, session);
  if (date.length > 0) {
    return `${date} ${formatTime(d, session)}`
  }
  return "";
}


export const formatDate = (d: Date | string | null, session: ISessionState) => {
  if (d == null) {
    return "";
  }

  const theDate = new Date(d);

  if (isInvalidDate(theDate)) {
    return "";
  }

  let format = defaultDateFormat;

  if (session.current.site) {
    if (session.current.site.preferences) {
      if (session.current.site.preferences.date_format != null) {
        format = session.current.site.preferences.date_format;
      }
    }
  }

  let dateString = format.split(" ")[0];
  //%d/%m/%Y %h:%i:%s
  dateString = dateString.replace("%D", theDate.getDate().toString().padStart(2, "0"));
  dateString = dateString.replace("%d", theDate.getDate().toString().padStart(2, "0"));
  dateString = dateString.replace("%m", (theDate.getMonth() + 1).toString().padStart(2, "0"));
  dateString = dateString.replace("%M", (theDate.getMonth() + 1).toString().padStart(2, "0"));
  dateString = dateString.replace("%Y", theDate.getFullYear().toString());
  dateString = dateString.replace("%y", (theDate.getFullYear() - 2000).toString().padStart(2, "0"));
  return dateString.split(" ")[0];
}
export const formatTime = (d: Date | string | null, session: ISessionState) => {
  if (d == null) {
    return "";
  }
  const theDate = new Date(d);

  let format = defaultDateFormat;

  if (session.current.site) {
    if (session.current.site.preferences) {
      if (session.current.site.preferences.date_format != null) {
        format = session.current.site.preferences.date_format;
      }
    }
  }

  let timeString = format.split(" ")[1];
  timeString = timeString.replace("%H", theDate.getHours().toString().padStart(2, "0"));
  timeString = timeString.replace("%h", theDate.getHours().toString().padStart(2, "0"));
  timeString = timeString.replace("%i", theDate.getMinutes().toString().padStart(2, "0"));
  timeString = timeString.replace("%I", theDate.getMinutes().toString().padStart(2, "0"));
  timeString = timeString.replace("%s", theDate.getSeconds().toString().padStart(2, "0"));
  timeString = timeString.replace("%S", theDate.getSeconds().toString().padStart(2, "0"));
  return timeString;
}


export const isInvalidDate = (d: Date | string | null) => {
  if (d == null) {
    return true;
  }
  d = new Date(d);
  if (isNaN(d.getTime())) {
    return true;
  }
  if (d.getFullYear() < 2000) {
    return true;
  }
  return false;
}

export const formatAssetVehicle = (
  vehicle: AssetVehicle,
  includeReg = false,
  includeColor = true,
  includeMake = true,
  includeModel = true) => {
  return [
    includeReg ? vehicle.registration : null,
    includeColor ? vehicle.colour : null,
    includeMake ? vehicle.make : null,
    includeModel ? vehicle.model : null,
  ].map((s) => s?.toString()?.trim()).filter(Boolean).join(", ");
};

export const formatVehicle = (vehicle: AssetVehicle) => formatAssetVehicle(vehicle);

export const formatVehicleWithoutColour = (vehicle: AssetVehicle) => formatAssetVehicle(vehicle, false, false);

export const formatVehicleFull = (vehicle: AssetVehicle) => formatAssetVehicle(vehicle, true);

export const locationPathTextToFriendly = (locationText: string) => {
  if (locationText == null) {
    return "";
  }
  if (locationText.length > 0) {
    locationText = locationText.substring(1);
  }
  if (locationText.endsWith("/")) {
    locationText = locationText.substring(0, locationText.length - 1);
  }
  locationText = locationText.replace(new RegExp(/\//g), " / ");
  return locationText;
}

export const formatString = (value: string) => {
  const bold1 = "<b>";
  const bold2 = "<_b>";

  value = value.replaceAll(new RegExp(/<b>/g), bold2);
  value = value.replaceAll(new RegExp(/<\/b>/g), bold1);

  const parts = value.split(bold1);
  const elements = [];

  for (let i = 0; i < parts.length; i++) {
    if (parts[i].indexOf(bold2) != -1) {
      elements.push(parts[i].split(bold2)[0]);
      elements.push(<b>{parts[i].split(bold2)[1]}</b>);
    } else {
      elements.push(parts[i]);
    }
  }


  return React.Children.map(elements, (element, i) => {
    return <Fragment key={i}>{element}</Fragment>;
  });
}

export const formatCamel = (name: string) => {
  name = name == null ? "" : name;

  let str = "";
  for (let i = 0; i < name.length; i++) {
    if (i == 0) {
      str = str + name[i].toUpperCase();
    } else {

      if (name[i].charCodeAt(0) >= 65 && (name[i].charCodeAt(0) <= 90)) {
        str = str + " " + name[i];
      } else {
        str = str + name[i];
      }
    }
  }
  return str;
}

/**
 * Maps role names to user-friendly descriptions by removing prefixes and formatting.
 *
 * Examples:
 * - "ParkIT.team_lead" → "Team Lead"
 * - "team_lead" → "Team Lead"
 * - "bus_driver" → "Bus Driver"
 */
export const mapRoleNameToDescription = (input: string): string => {
  let target: string;

  if (input.includes(".")) {
    const parts = input.split(".");
    // Drop the first segment (remove first dot prefix)
    target = parts.slice(1).join("");
  } else {
    // Use the full string if there's no dot
    target = input;
  }

  // Replace underscores with spaces
  target = target.replace(/_/g, " ");

  // Collapse multiple spaces and trim
  target = target.replace(/\s+/g, " ").trim();

  // Capitalize each word
  const formatted = target
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

  return formatted;
};

export const isTransportManager = (session: ISessionState) => {
  if (session.current.site) {
    //System admin OR Transport manager.
    return isSysAdmin(session) || session.current.site.user.transportManager == true;
  }
  return false;
}

export const isSysAdmin = (session: ISessionState) => {
  if (session.current.site) {
    //System admin must be defined as both credential system admin and user admin.
    return session.current.site.user.credentialType == CredentialType.systemAdmin && session.current.site.user.userType == UserType.admin;
  }
  return false;
}

export const isAdmin = (session: ISessionState) => {
  if (isSysAdmin(session)) {
    return true;
  }
  if (session.current.site) {
    return session.current.site.user.userType == UserType.admin;
  }
  return false;
}

export const hasKeyMasterPermissions = (session: ISessionState): boolean => {
  return (
    session?.current?.site?.user?.keyMaster === true ||
    isAdmin(session) ||
    isSysAdmin(session)
  );
};

export const getAccountType = (session: ISessionState) => {
  if (session.current.site) {
    return session.current.site.user.userType;
  }
  return UserType.standard;
}


export const getFileUrl = (session: ISessionState, fileName: string) => {
  fileName = fileName ?? "";
  if (fileName.length == 0) return "";
  return `${session.current.context.url.replace("/api", "/fs/")}${fileName}`;
}

export const getAPIRootUrl = (session: ISessionState) => {
  return `${session.current.context.url.replace("/api", "")}`;
}

export const paymentTypeLabelMap = new Map<PaymentTypes, string>([
  [PaymentTypes.account, 'On Account'],
  [PaymentTypes.cash, 'Cash'],
  [PaymentTypes.compDiscount, 'Complimentary'],
  [PaymentTypes.creditCard, 'Credit Card'],
  [PaymentTypes.creditDebitCard, 'Credit/Debit Card'],
  [PaymentTypes.debitCard, 'Debit Card'],
  [PaymentTypes.refund, 'Refund']
]);


interface ApiRequestHandlerBase<TParams, TReturn> {
  data: TReturn;
  status: RequestStatus;
  err: Error | unknown | null;
  params: TParams | null;
  callBack: (value: TReturn) => void;
  context: IContext | null;
}

interface ApiRequestHandlerCallback<TParams, TReturn> extends ApiRequestHandlerBase<TParams, TReturn> {
  send: (context: IContext, parameters: TParams, callBack?: (value: TReturn) => void) => void;
}

interface ApiRequestHandlerAsync<TParams, TReturn> extends ApiRequestHandlerBase<TParams, TReturn> {
  send: (context: IContext, parameters: TParams, callBack?: (value: TReturn) => void) => Promise<TReturn>;
}

export function promisifyApi<TParams, TReturn>(
  thing: ApiRequestHandlerCallback<TParams, TReturn>,
): ApiRequestHandlerAsync<TParams, TReturn> {
  const send = (context: IContext, params: TParams) => {
    return new Promise<TReturn>(
      (resolve) => thing.send(context, params, resolve)
    );
  }

  return {
    ...thing,
    send: send,
  }
}

/**
 * handles the timing issue where React's setState is async by pushing the error on next tick
 * TODO: DD - might not be necessary unless we want to promisify the api hooks
**/
export function useApiWithReject<TParams, TReturn>(
  thing: ApiRequestHandlerCallback<TParams, TReturn>
): { send: (context: IContext, params: TParams) => Promise<NonNullable<TReturn>> } {
  const apiRef = useRef(thing);
  apiRef.current = thing;

  const send = useCallback((context: IContext, params: TParams): Promise<NonNullable<TReturn>> => {
    return new Promise((resolve, reject) => {
      thing.send(context, params, (result: TReturn) => {
        if (result === null || result === undefined) {
          // we have to push it on next tick as we need to wait for React setState to complete, then read error from ref
          setTimeout(() => {
            const err = apiRef.current.err;
            if (err instanceof Error) {
              reject(err);
            } else if (err && typeof err === "object" && "message" in err) {
              reject(new Error(String((err as { message: unknown }).message)));
            } else {
              reject(new Error("API request failed"));
            }
          }, 0);
        } else {
          resolve(result as NonNullable<TReturn>);
        }
      });
    });
  }, [thing]);

  return {
    ...thing,
    send: send,
  };
}

export function enumToSelectOptions(
  value: Record<string | number, string | number>,
  options?: {
    indexAsValue?: boolean,
    selectText?: string | null,
  }
): IDropDownListItem[] {
  const selectOptions = Object
    .entries(value)
    .filter(([key]) => isNaN(Number.parseInt(key)))
    .map(([key, value]) => ({
      name: formatCamel(key),
      value: options?.indexAsValue
        ? value.toString()
        : key,
    }));

  if (options?.selectText) {
    selectOptions.unshift({
      name: options.selectText,
      value: "",
    });
  }

  return selectOptions;
}

export function capitalise([firstLetter, ...word]: string): string {
  return firstLetter.toUpperCase() + word.join('')
}

export function round2(value: number): number {
  return Number(value.toFixed(2));
}

export const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
